import { EntityPropertyDefinitions } from '@/types/eds/Definitions';
import type { EdsFilterKeyTypeRecord } from '@/features/eds/services/EdsFiltersService/types/EdsFilterKeyTypeRecord';
import type { EdsFilterOptionsRecord } from '@/features/eds/services/EdsFiltersService/types/EdsFilterOptionsRecord';
import type { EDSFilterableFields } from '@/features/eds/services/types/EDSFilterableFields';
import type {
  EDSActiveFilter,
  EDSFilterPrimitiveOrOptionsValue,
  EDSFilterPrimitiveValue,
  EDSFilterRangeValue,
  EDSFilterState,
  EDSFiltersState,
} from '@/features/eds/types/EDSFilterConfig';
import type { EdsStoredSorting } from '@/features/eds/types/EdsSortingConfig';
import type { EDSFiltersRequestFiltersList } from '@/features/eds/types/api/requests/EDSFiltersRequestBody';
import dayjs from 'dayjs';
import {
  EDS_FILTER_ASSIGNEE_UNASSIGNED_LABEL,
  EDS_FILTER_ASSIGNEE_UNASSIGNED_VALUE,
} from '@/features/eds/constants/edsAssigneeFilter';
import {
  edsFilterArrayTypes,
  edsFilterBooleanTypes,
  edsFilterDateRangeTypes,
  edsFilterRangeTypes,
} from '@/features/eds/constants/edsFilterDefaultTypes';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { EdsFilterEntities } from '@/features/eds/enums/EdsFilterEntities';
import { EdsFilterType } from '@/features/eds/enums/EdsFilterType';
import { AbstractEdsFiltersStorage } from '@/features/eds/services/EdsFiltersService/classes/AbstractEdsFiltersStorage/AbstractEdsFiltersStorage';
import { EdsStorageService } from '@/features/eds/services/EdsStorageService/EdsStorageService';
import { edsToCodeFormat, edsToEntityFormat } from '@/features/eds/services/utils/edsToCodeFormat';
import { LocalStorageVariables } from '@/enums/LocalStorageVariables';
import { SortDirection } from '@/enums/SortDirection';
import DateService from '@/services/DateService/DateService';
import TypeService from '@/services/TypeService/TypeService';

export class EdsFilterUtilsService {
  public static prepareSavedFiltersForSending(filters: EDSFiltersState): EDSFiltersRequestFiltersList {
    const filtersWithMergedRelationalKeys = Object.keys(filters).reduce((acc, key) => {
      const filterValue = filters[key];
      const { isRelational, ...rest } = filterValue;
      if (!isRelational) {
        return { ...acc, [key]: filterValue };
      }
      const [relationalPrefix] = key.split('_');
      const existingRelation = acc[relationalPrefix] || { ...rest, isRelational, value: [] };
      return {
        ...acc,
        [relationalPrefix]: {
          ...existingRelation,
          value: [...existingRelation.value, ...(Array.isArray(rest.value) ? rest.value : [rest.value])].filter(
            Boolean,
          ),
        },
      };
    }, {});
    return Object.keys(filtersWithMergedRelationalKeys).reduce((acc, key) => {
      const { type, value, originalRelationshipKey } = filtersWithMergedRelationalKeys[key];
      const isArray = edsFilterArrayTypes.includes(type);
      const isBoolean = edsFilterBooleanTypes.includes(type);
      const isRange = edsFilterRangeTypes.includes(type);

      let preparedValue: any[] = [];

      if (isRange)
        preparedValue = (value as EDSFilterRangeValue)?.every((rangeValue) => !rangeValue)
          ? []
          : (value as EDSFilterRangeValue);
      else if (isArray)
        preparedValue = (value && Array.isArray(value) ? [...value].filter(Boolean) : [value].filter(Boolean)).map(
          (val) => (val === EDS_FILTER_ASSIGNEE_UNASSIGNED_VALUE ? null : val),
        );
      else if (isBoolean) preparedValue = [value === 'true'];
      else preparedValue = [value].filter(Boolean);

      if (preparedValue.length) {
        return [
          ...acc,
          {
            propertyName: edsToEntityFormat(originalRelationshipKey || key),
            values: preparedValue.flatMap((value) => value),
          },
        ];
      }

      return acc;
    }, [] as EDSFiltersRequestFiltersList);
  }

  public static convertSentFiltersToSavedFilters(
    filters: EDSFiltersRequestFiltersList,
  ): Record<string, EDSFilterPrimitiveOrOptionsValue> {
    return filters.reduce(
      (acc, { propertyName, values }) => {
        return {
          ...acc,
          [propertyName]: values.map((value) => (value === null ? EDS_FILTER_ASSIGNEE_UNASSIGNED_VALUE : value)),
        } as Record<string, EDSFilterPrimitiveOrOptionsValue>;
      },
      {} as Record<string, EDSFilterPrimitiveOrOptionsValue>,
    );
  }

  public static prepareCustomFiltersForSending(
    filterFields: Partial<Record<EdsFilterEntities, string[]>>,
  ): EDSFiltersRequestFiltersList {
    return Object.keys(filterFields).map((item) => ({ propertyName: item, values: filterFields[item] }));
  }

  public static prepareFiltersForSaving(
    filters: EDSFiltersState,
    storage: AbstractEdsFiltersStorage<any, any>,
  ): { key: string; value: EDSFilterPrimitiveOrOptionsValue }[] {
    return Object.keys(filters).reduce(
      (acc, key) => {
        const value = filters[key].value;

        if (!EdsFilterUtilsService.checkIsEmptyValue(value)) return acc;

        return [
          ...acc,
          {
            key,
            value: storage.convertValueToStorageFormat(filters[key].value),
          },
        ];
      },
      [] as { key: string; value: EDSFilterPrimitiveOrOptionsValue }[],
    );
  }

  public static injectFilterValueToTheArray(
    value: EDSFilterPrimitiveValue,
    array: EDSFilterPrimitiveValue[],
  ): EDSFilterPrimitiveValue[] {
    if (array.includes(value)) {
      return [...array].filter((val) => val !== value);
    }
    return [...array, value];
  }

  public static prepareFilterParamKey(prefix: string, propertyName: string) {
    return `${prefix}-${propertyName}`;
  }

  public static prepareOverridesWithHiddenFilters(properties: string[] | { propertyName: string }[]) {
    return properties.reduce((acc, property) => {
      if (typeof property === 'object')
        return { ...acc, [property.propertyName]: { filterConfiguration: { hidden: true } } };
      return { ...acc, [property]: { filterConfiguration: { hidden: true } } };
    }, {});
  }

  public static checkFilterIsActive(
    params: Record<string, string>,
    filters: Record<string, string | number | boolean | undefined>,
    onlyPassed: boolean = true,
  ): boolean {
    const activeFiltersAmount = Object.keys(params).filter((paramKey) => Boolean(params[paramKey])).length;

    return (
      Object.keys(filters).every((key) => {
        return key in params && Boolean(filters[key]) && params[key].toString() === filters[key]?.toString();
      }) && (onlyPassed ? activeFiltersAmount === Object.keys(filters).length : true)
    );
  }

  public static checkIsEmptyValue(value: EDSFilterPrimitiveOrOptionsValue): boolean {
    if (Array.isArray(value)) return value.filter((filterValue) => Boolean(filterValue)).length > 0;
    return Boolean(value);
  }

  public static getKeyTypeList(filterableFieldsRecord: EDSFilterableFields): EdsFilterKeyTypeRecord {
    return Object.keys(filterableFieldsRecord).reduce(
      (acc, filterKey) => ({ ...acc, [filterKey]: filterableFieldsRecord[filterKey].filterType }),
      {},
    );
  }

  public static prepareActiveFilters(
    entityConfig: { entityCode: EdsEntityCodes; section?: EdsDefinitionSections },
    filters: EDSFiltersState,
    sorting?: EdsStoredSorting,
    relationships?: EdsFilterOptionsRecord,
  ): EDSActiveFilter[] {
    const { entity: definition } = EdsStorageService.getEntity(entityConfig.entityCode, entityConfig?.section);
    const rawUserOptions = localStorage.getItem(LocalStorageVariables.FilterUserOptions);
    const userOptions = rawUserOptions
      ? JSON.parse(rawUserOptions)?.map((user) => ({
          value: String(user.id),
          label: [user.firstName, user.lastName].join(' '),
        }))
      : [];

    const extractFieldFromDefinition = (fieldName: string) =>
      (definition?.fields?.[edsToCodeFormat(fieldName)] as EntityPropertyDefinitions) ||
      ({} as EntityPropertyDefinitions);

    const prepareActiveFilterLabel = (label: string, value: EDSFilterPrimitiveValue, valueLabel?: string): string => {
      if (!value) return label;
      if (TypeService.isBoolean(value) || value === 'true') return label;
      if (TypeService.isValidISODateString(value)) return `${label}: ${dayjs(new Date(value)).format('DD-MM-YYYY')}`;
      return `${valueLabel || String(value)}`;
    };

    const prepareAssigneeLabel = (value: number | string, defaultLabel?: string) => {
      if (value === EDS_FILTER_ASSIGNEE_UNASSIGNED_VALUE) return EDS_FILTER_ASSIGNEE_UNASSIGNED_LABEL;
      const user = userOptions?.find((user) => String(user.value) === String(value));
      if (user?.label) return user.label;

      return defaultLabel;
    };

    const processArray = (
      filterKey: string,
      { label, value, hidden, options, type }: EDSFilterState,
    ): EDSActiveFilter[] => {
      const arrayValue = Array.isArray(value) ? value : [value];

      return arrayValue.length
        ? arrayValue.map((val) => {
            const isAssignee = type === EdsFilterType.Assignee;
            const valueLabel = (isAssignee ? userOptions : options).find((option) => {
              try {
                return option.value?.toString() == (val as EDSFilterPrimitiveValue)?.toString();
              } catch {
                return 'Unspecified';
              }
            })?.label;

            return {
              label: isAssignee
                ? prepareAssigneeLabel(val as string, label)
                : prepareActiveFilterLabel(label, val as EDSFilterPrimitiveValue, valueLabel),
              key: filterKey,
              value: val,
              disabled: hidden,
            };
          })
        : [];
    };

    const processRange = (filterKey: string, { type, label, value, hidden }: EDSFilterState): EDSActiveFilter[] => {
      const isDateRangeValue = edsFilterDateRangeTypes.includes(type);
      const rangeValue = Array.isArray(value) ? value : [value];
      const formattedRangeValue = rangeValue
        .map((val) => {
          if (val)
            return isDateRangeValue && DateService.checkIsValidDate(val)
              ? `${dayjs(new Date(val)).format('DD-MM-YYYY')}`
              : val;
          return 'N/A';
        })
        .join(' — ');

      const formattedLabel = `${label}: ${formattedRangeValue}`;

      return rangeValue.every((rangeValue) => !rangeValue)
        ? []
        : [
            {
              label: formattedLabel,
              key: filterKey,
              value,
              disabled: hidden,
            },
          ];
    };

    const processSimple = (filterKey: string, { value, label, hidden, type }: EDSFilterState): EDSActiveFilter[] => {
      if (!value) return [];

      return [
        {
          label:
            type === EdsFilterType.Assignee
              ? prepareAssigneeLabel(value as string, label)
              : prepareActiveFilterLabel(label, value as EDSFilterPrimitiveValue),
          key: filterKey,
          value,
          disabled: hidden,
        },
      ];
    };

    const activeFilters = Object.keys(filters).reduce((acc, filterKey) => {
      const filter = filters[filterKey];

      if ([...edsFilterArrayTypes, ...edsFilterRangeTypes].includes(filter.type) || filter.isRelational) {
        filter.value = Array.isArray(filter.value) ? (filter.value as any) : ([filter.value] as any);
      }

      if (relationships && filter.isRelational) {
        const relationKey = Object.keys(relationships).find((key) => key.toString() === filterKey.toString()) || '';
        filter.options = relationships?.[filterKey] || relationships[relationKey] || filter.options;
      }

      if (Array.isArray(filter.value)) {
        if (edsFilterRangeTypes.includes(filter.type)) {
          return [...acc, ...processRange(filterKey, filter)];
        } else {
          return [...acc, ...processArray(filterKey, filter)];
        }
      }

      return [...acc, ...processSimple(filterKey, filter)];
    }, [] as EDSActiveFilter[]);

    if (sorting?.sortDirection && sorting?.sortBy) {
      const field = extractFieldFromDefinition(sorting.sortBy);
      const fieldLabel = field?.sortingConfiguration?.label || field?.displayTitle || sorting.sortBy;
      const directionLabel =
        sorting.sortDirection === SortDirection.ASC
          ? field?.sortingConfiguration?.ascLabel
          : field?.sortingConfiguration?.descLabel;

      activeFilters.push({
        label: `Sort By: ${fieldLabel} - ${directionLabel || sorting.sortDirection}`,
        value: sorting.sortDirection,
        disabled: false,
        key: sorting.sortBy,
        isSorting: true,
      });
    }

    return activeFilters.filter((activeFilter) => Boolean(activeFilter.value));
  }

  public static getDefaultValueByType(type: EdsFilterType) {
    const shouldReturnArray = edsFilterArrayTypes.includes(type);
    const shouldReturnBoolean = edsFilterBooleanTypes.includes(type);
    const shouldReturnRange = edsFilterRangeTypes.includes(type);

    switch (true) {
      case shouldReturnArray:
        return [];
      case shouldReturnBoolean:
        return false;
      case shouldReturnRange:
        return [null, null];
      default:
        return '';
    }
  }

  public static transformValueToFilterFormatByType(
    value: unknown,
    type: EdsFilterType,
  ): EDSFilterPrimitiveOrOptionsValue {
    const shouldReturnArray = edsFilterArrayTypes.includes(type);
    const shouldReturnBoolean = edsFilterBooleanTypes.includes(type);
    const shouldReturnRange = edsFilterRangeTypes.includes(type);

    switch (true) {
      case shouldReturnArray:
        return Array.isArray(value) ? value : ([value] as any);
      case shouldReturnBoolean:
        return TypeService.isBoolean(value) ? (value as any) : Boolean(value);
      case shouldReturnRange:
        return Array.isArray(value) ? value : ([value, null] as EDSFilterRangeValue);
      default:
        return String(value) || '';
    }
  }

  public static sortByDisplayOrder(filter1: EDSFilterState, filter2: EDSFilterState) {
    if (filter1.displayOrder > filter2.displayOrder) return 1;
    if (filter1.displayOrder < filter2.displayOrder) return -1;
    return 0;
  }

  public static filterHidden(filter: EDSFilterState) {
    return !filter.hidden;
  }

  public static arePrevAndSavedFiltersEqual(
    prevFilters: { key: string; value: EDSFilterPrimitiveOrOptionsValue }[],
    currentFilters: { key: string; value: EDSFilterPrimitiveOrOptionsValue }[],
  ): boolean {
    const normalize = (filters: { key: string; value: EDSFilterPrimitiveOrOptionsValue }[]) =>
      Object.fromEntries(
        filters
          .slice()
          .sort((a, b) => a.key.localeCompare(b.key))
          .map(({ key, value }) => [key, JSON.stringify(value)]),
      );

    const normalizedPrevFilters = normalize(prevFilters);
    const normalizedCurrentFilters = normalize(currentFilters);

    return JSON.stringify(normalizedPrevFilters) === JSON.stringify(normalizedCurrentFilters);
  }

  public static getMissingFilterKeys(
    prevFilters: { key: string; value: EDSFilterPrimitiveOrOptionsValue }[],
    currentFilters: { key: string; value: EDSFilterPrimitiveOrOptionsValue }[],
  ): string[] {
    const targetKeys = new Set(currentFilters.map((item) => item.key));
    return prevFilters.map((item) => item.key).filter((key) => !targetKeys.has(key));
  }
}
